import EventEmitter from '../helpers/EventEmmiter';
import APIController from './api';
import { loadJsFile } from 'lib/helpers/common';

const LIVEKIT_URL = 'https://www.eyespy360.com/library/livekit-client.umd.js';

const COMMAND_TOPIC = 'agent-control';
const RESPONSE_TOPIC = 'agent-response';

// Gestures we retry room.startAudio() on when the browser blocks playback
const UNLOCK_EVENTS = ['pointerdown', 'touchend', 'keydown'];
// LITE: how long the command socket may take to report 'connected' before
// the avatar counts as failed (the guide then goes voice-only)
const WS_READY_TIMEOUT_MS = 8000;

class HeyGenManager extends EventEmitter {
  constructor(videoEl, emitEvent, mode = 'FULL') {
    super();
    this._videoEl = videoEl;
    this._emitEvent = emitEvent;
    this._mode = mode === 'LITE' ? 'LITE' : 'FULL';
    this._sessionId = null;
    this._sessionToken = null;
    this._room = null;
    this._textBuffer = '';
    this._flushTimer = null;
    this._connected = false;
    this._currentResponseId = null; // response the text deltas currently belong to
    this._killedResponseId = null;  // interrupted response — its remaining deltas are dropped
    this._speakEventId = null; // LITE: one event_id per speak sequence
    this._ws = null;           // LITE: command WebSocket (agent.speak & co.)
    this._wsReady = false;     // LITE: session.state_updated === 'connected' received
    this._gestureUnlock = null; // pending document listener retrying startAudio()
    this._closed = false;       // disconnect() called — a Disconnected event is expected
  }

  get isLite() {
    return this._mode === 'LITE';
  }

  // Resolves true once the LiveKit room is connected, false on any failure
  // (or when disconnect() was called meanwhile) — never rejects. The tour
  // guide waits on it to decide between avatar and voice-only output.
  async connect(avatarId, quality = 'medium', sandbox = false) {
    try {
      console.log('[heygen] connecting, avatar:', avatarId, this._mode, sandbox ? '(sandbox)' : '');
      const sessionData = await APIController.newHeyGenSession(avatarId, quality, sandbox, this._mode);
      if (!sessionData) throw new Error('No session data from LiveAvatar');

      const { session_id, session_token, livekit_url, livekit_client_token, ws_url } = sessionData;
      if (this._closed) {
        // gave up on us (timeout / panel closed) while the session was created
        if (session_token) APIController.stopHeyGenSession(session_id, session_token).catch(() => {});
        return false;
      }
      this._sessionId = session_id;
      this._sessionToken = session_token;

      // LITE commands travel over a dedicated WebSocket, not the LiveKit data
      // channel (which the avatar only uses in FULL mode).
      if (this.isLite) {
        if (!ws_url) {
          // nothing could ever reach the avatar — let the guide go voice-only
          console.warn('[heygen][mode] LITE: no ws_url in session data — avatar cannot receive audio');
          throw new Error('LITE session without ws_url');
        }
        this._openCommandSocket(ws_url);
      }

      if (!window.LivekitClient) await loadJsFile(LIVEKIT_URL);
      const { Room, RoomEvent } = window.LivekitClient;
      this._room = new Room();

      this._room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        if (participant.identity !== 'heygen') return;
        if (this._videoEl && (track.kind === 'video' || track.kind === 'audio')) {
          track.attach(this._videoEl);
          console.log(`[heygen] ${track.kind} track subscribed and attached`);
          if (track.kind === 'video') {
            this._emitEvent('heygen:stream', this._videoEl);
          }
        }
      });

      // Both modes play the voice through this room's audio track, so blocked
      // playback is total silence while the video keeps running. LiveKit tells
      // us when that happens — retry from the next user gesture.
      this._room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (this._room.canPlaybackAudio) {
          console.log('[heygen] audio playback allowed');
          this._removeGestureUnlock();
          this._emitEvent('heygen:audio_unlocked');
          return;
        }
        console.warn('[heygen] audio playback blocked by the browser — waiting for a user gesture');
        this._addGestureUnlock();
        // iOS: the Play tap was used up before the room existed — ask for one more
        this._emitEvent('heygen:audio_blocked');
      });

      this._room.on(RoomEvent.DataReceived, (data, _participant, _kind, topic) => {
        if (topic !== RESPONSE_TOPIC) return;
        try {
          const event = JSON.parse(new TextDecoder().decode(data));
          const type = event.event_type || event.type; // FULL uses event_type, LITE uses type
          if (type === 'avatar.speak_started' || type === 'agent.speak_started') {
            this._emitEvent('voice:speaking');
            this._emitEvent('avatar:speak_started');
          } else if (type === 'avatar.speak_ended' || type === 'agent.speak_ended') {
            this._emitEvent('voice:done');
            this._emitEvent('voice:listening');
          }
        } catch (_) {}
      });

      this._room.on(RoomEvent.Disconnected, (reason) => {
        const wasConnected = this._connected;
        this._connected = false;
        // unexpected = the room dropped on its own (LiveKit gave up
        // reconnecting, avatar session ended server-side), not our disconnect()
        this._emitEvent('heygen:disconnected', { unexpected: !this._closed && wasConnected, reason });
      });

      const room = this._room;
      await room.connect(livekit_url, livekit_client_token);
      if (this._closed || this._room !== room) {
        try { room.disconnect(); } catch (_) {}
        return false;
      }
      // LITE: audio sent before the command socket reports 'connected' is
      // dropped — the opening greeting was lost. Ready only once it is.
      if (this.isLite && !(await this._waitWsReady())) {
        throw new Error(`LITE command socket not connected after ${WS_READY_TIMEOUT_MS} ms`);
      }
      if (this._closed || this._room !== room) return false;
      this._connected = true;
      console.log('[heygen] ready, session:', this._sessionId);
      this._emitEvent('heygen:ready');
      return true;
    } catch (err) {
      console.error('[heygen] connect failed:', err);
      this._emitEvent('heygen:error', err);
      return false;
    }
  }

  get connected() {
    return this._connected;
  }

  _newEventId() {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  }

  // Listen until playback is allowed — AudioPlaybackStatusChanged removes this
  // again once startAudio() succeeds.
  _addGestureUnlock() {
    if (this._gestureUnlock) return;
    this._gestureUnlock = () => {
      if (!this._room) return;
      this._room.startAudio()
        .then(() => console.log('[heygen] audio started after user gesture'))
        .catch((err) => console.warn('[heygen] startAudio failed:', err));
    };
    UNLOCK_EVENTS.forEach((e) => document.addEventListener(e, this._gestureUnlock, { passive: true }));
  }

  _removeGestureUnlock() {
    if (!this._gestureUnlock) return;
    UNLOCK_EVENTS.forEach((e) => document.removeEventListener(e, this._gestureUnlock));
    this._gestureUnlock = null;
  }

  // Resolves true when the LITE session state reaches 'connected', false on
  // close / timeout.
  _waitWsReady() {
    if (this._wsReady) return Promise.resolve(true);
    return new Promise((resolve) => {
      const timer = setTimeout(() => { this._onWsReady = null; resolve(false); }, WS_READY_TIMEOUT_MS);
      this._onWsReady = (ok) => { clearTimeout(timer); this._onWsReady = null; resolve(ok); };
    });
  }

  _openCommandSocket(wsUrl) {
    this._ws = new WebSocket(wsUrl);
    this._ws.onopen = () => console.log('[heygen][mode] LITE: command WebSocket open, waiting for session state…');
    this._ws.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        if (event.state) {
          // session.state_updated handshake
          const ready = event.state === 'connected';
          if (ready !== this._wsReady) {
            this._wsReady = ready;
            console.log(`[heygen][mode] LITE: session state → ${event.state}`);
          }
          if (ready && this._onWsReady) this._onWsReady(true);
          return;
        }
        const type = event.type || event.event_type;
        if (type === 'agent.speak_started') {
          this._emitEvent('voice:speaking');
          this._emitEvent('avatar:speak_started');
        } else if (type === 'agent.speak_ended') {
          this._emitEvent('voice:done');
          this._emitEvent('voice:listening');
        }
      } catch (_) {}
    };
    this._ws.onerror = (e) => console.error('[heygen][mode] LITE: command WebSocket error', e);
    this._ws.onclose = () => {
      this._wsReady = false;
      if (this._onWsReady) this._onWsReady(false);
      console.log('[heygen][mode] LITE: command WebSocket closed');
    };
  }

  // LITE: send one command over the dedicated WebSocket
  _sendWs(payload) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      console.warn('[heygen][mode] LITE: dropping command — WebSocket not open:', payload.type);
      return;
    }
    if (!this._wsReady) {
      console.warn('[heygen][mode] LITE: dropping command — session not in connected state yet:', payload.type);
      return;
    }
    this._ws.send(JSON.stringify(payload));
  }

  _publish(payload) {
    if (!this._room || this._room.state !== 'connected') return;
    this._room.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(payload)),
      { reliable: true, topic: COMMAND_TOPIC }
    );
  }

  // Speak a complete message directly (room messages, not streaming deltas).
  // LITE has no platform TTS — hand the text back so the tour guide can
  // synthesize it through the OpenAI Realtime session (audio then flows in
  // via speakAudio()).
  speak(text) {
    if (!this._connected || !text) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.isLite) {
      console.log('[heygen][mode] LITE: routing scripted text through OpenAI TTS instead of platform TTS');
      this._emitEvent('heygen:speak_text', trimmed);
      return;
    }
    console.log('[heygen][mode] FULL: sending avatar.speak_text (LiveAvatar platform TTS will speak)');
    this._publish({ event_type: 'avatar.speak_text', event_id: this._newEventId(), text: trimmed });
  }

  // LITE: forward one base64 PCM16@24kHz chunk of our own synthesized voice
  speakAudio(base64Audio) {
    if (!this._connected || !base64Audio) return;
    if (!this._speakEventId) {
      this._speakEventId = this._newEventId();
      console.log(`[heygen][mode] LITE: streaming agent.speak audio chunks over WS (event ${this._speakEventId})`);
    }
    this._sendWs({ type: 'agent.speak', event_id: this._speakEventId, audio: base64Audio });
  }

  // LITE: close the current speak sequence
  speakEnd() {
    if (!this._connected || !this._speakEventId) return;
    this._sendWs({ type: 'agent.speak_end', event_id: this._speakEventId });
    this._speakEventId = null;
  }

  interrupt() {
    clearTimeout(this._flushTimer);
    this._textBuffer = '';
    // Deltas of the interrupted answer keep arriving until OpenAI winds the
    // response down. Without this they land back in the buffer and flush as new
    // avatar.speak_text events, so the avatar picks the killed answer up again.
    // Keyed by response id (not a flag) so a delta from the NEXT response is
    // never suppressed — if the id is ever absent, behaviour is unchanged.
    this._killedResponseId = this._currentResponseId;
    if (!this._connected) return;
    if (this.isLite) {
      const id = this._speakEventId || this._newEventId();
      this._speakEventId = null;
      this._sendWs({ type: 'agent.interrupt', event_id: id });
      return;
    }
    this._publish({ event_type: 'avatar.interrupt', event_id: this._newEventId() });
  }

  // Called by RealtimeEventHandler on each response.text.delta (FULL only —
  // LITE responses are audio, there are no text deltas to forward)
  pushText(delta, responseId = null) {
    if (!this._connected || this.isLite || !delta) return;
    if (responseId && responseId === this._killedResponseId) return;
    if (responseId) this._currentResponseId = responseId;
    this._textBuffer += delta;
    if (/[.!?。]/.test(delta)) {
      this._flush();
    } else {
      clearTimeout(this._flushTimer);
      this._flushTimer = setTimeout(() => this._flush(), 300);
    }
  }

  // Called on response.text.done to flush any remaining buffer
  flushAll(responseId = null) {
    clearTimeout(this._flushTimer);
    if (responseId && responseId === this._killedResponseId) {
      this._textBuffer = '';
      return;
    }
    this._flush();
  }

  _flush() {
    const text = this._textBuffer.trim();
    // Cleared even when unsendable: a buffer held across a room drop would be
    // spoken later, out of context.
    this._textBuffer = '';
    if (!text || !this._room || this._room.state !== 'connected') return;
    const eventId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
    const payload = new TextEncoder().encode(JSON.stringify({
      event_type: 'avatar.speak_text',
      event_id: eventId,
      text,
    }));
    this._room.localParticipant.publishData(payload, { reliable: true, topic: COMMAND_TOPIC });
  }

  async disconnect() {
    // Silence the avatar BEFORE anything that can block. stopHeyGenSession() is
    // a network round-trip and the LiveKit room keeps playing the avatar's voice
    // for its whole duration, so closing the panel left it talking. Interrupt
    // first (it needs its transport alive — before _connected = false and before
    // the WS closes), tear the room down synchronously, then do the slow call.
    if (this._connected) this.interrupt();
    this._closed = true;
    this._connected = false;
    clearTimeout(this._flushTimer);
    this._textBuffer = '';
    this._removeGestureUnlock();
    if (this._room) {
      this._room.disconnect();
      this._room = null;
    }
    if (this._videoEl) {
      this._videoEl.srcObject = null;
    }
    if (this._ws) {
      try { this._ws.close(); } catch (_) {}
      this._ws = null;
      this._wsReady = false;
    }
    const token = this._sessionToken;
    const id = this._sessionId;
    this._sessionToken = null;
    this._sessionId = null;
    if (token) {
      try { await APIController.stopHeyGenSession(id, token); } catch (_) {}
    }
  }
}

export default HeyGenManager;
